import { useCallback, useMemo } from 'react';
import {
  getCellWidths,
  getColumnByType,
  getRowCells,
  getRowHeight,
  getTextHeightForParagraph,
  updateDataByType
} from '../utils/functions';
import commonStore from '../../../../store/common';
import { CustomHeaderWithDrillDown } from '../../components/CustomHeaderWithDrillDown';
import { useSearchParams } from 'react-router-dom';
import { useSyncExternalStore } from 'react';
import { reaction } from 'mobx';
import reportStore from '../../store';
import { isEsmaTableDisabledForFund } from '../utils/visibleSections';

export default function useSplitedTableByColumn({
  data,
  onChangeFund,
  schema,
  isStaticTemplate,
  requiredFields,
  funds,
  isMultiFunds,
  tables,
  tableName,
  isSingleData = false
}) {
  const [searchParams] = useSearchParams();
  const currentDataKey = searchParams.get('fund_id');
  const isTableDisabledForFund = fundOrId => {
    const fund =
      typeof fundOrId === 'object' && fundOrId !== null
        ? fundOrId
        : funds[String(fundOrId)] || funds[fundOrId];
    return isEsmaTableDisabledForFund(fund, tableName);
  };
  const isComparisonReport = useSyncExternalStore(
    subscribe => {
      const dispose = reaction(() => reportStore.isComparisonReport, subscribe, {
        fireImmediately: false
      });
      return dispose;
    },
    () => reportStore.isComparisonReport
  );
  const onChange = useCallback(
    value => {
      const changes = value[0];
      const fund = isMultiFunds
        ? JSON.parse(JSON.stringify(funds[changes.newCell.fundId].fund_data[tableName]))
        : JSON.parse(JSON.stringify(data));

      fund[changes.newCell.dataIndex][changes.newCell.dataKey] = updateDataByType({
        cell: changes.newCell
      });

      onChangeFund(tableName, fund, changes.newCell.fundId);
    },
    [data]
  );

  const contentWidth = useSyncExternalStore(
    subscribe => reaction(() => commonStore.contentWidth, subscribe, { fireImmediately: false }),
    () => commonStore.contentWidth
  );

  const columns = useMemo(() => {
    let _columns = [];
    if (!isMultiFunds) {
      _columns = isComparisonReport
        ? Array.from(Array(3).keys()).map(index => ({
            columnId: index + 1,
            width: contentWidth / 3
          }))
        : Array.from(Array(2).keys()).map(index => ({
            columnId: index + 1,
            width: contentWidth / 2
          }));
    } else {
      const _fundSize = Object.keys(funds).length;
      _columns =
        !isComparisonReport && _fundSize <= 3
          ? Array.from(Array(_fundSize + 1).keys()).map(index => ({
              columnId: index + 1,
              width: contentWidth / (_fundSize + 1)
            }))
          : isComparisonReport
            ? [
                {
                  columnId: 1,
                  width: 200
                },
                ...Object.values(funds)
                  .map(item => [item, item.previous_fund])
                  .flat()
                  .map((_, index) => ({
                    columnId: index + 2,
                    width: 300
                  }))
              ]
            : [
                {
                  columnId: 1,
                  width: 200
                },
                ...Object.keys(funds).map((_, index) => ({
                  columnId: index + 2,
                  width: 300
                }))
              ];
    }
    return [
      {
        columnId: 0,
        resizable: true,
        width: 30
      },
      ..._columns
    ];
  }, [isMultiFunds, funds, contentWidth, isComparisonReport]);

  const _tables = useMemo(() => {
    if (!data) return [];

    const { labelWidth, valueWidth } = getCellWidths({
      isMultiFunds,
      fundSize: Object.keys(funds).length,
      contentWidth,
      isComparisonReport
    });

    return tables.map((value, i) => {
      const index = value?.dataIndex || i;
      let _rows = [];
      if (!isMultiFunds) {
        _rows = [
          {
            rowId: 0,
            height: getTextHeightForParagraph(value.name || '', labelWidth),
            cells: [
              {
                type: 'header',
                text: ''
              },
              {
                type: 'header',
                text: value.name
              },
              ...(isComparisonReport
                ? [
                    {
                      type: 'header',
                      text: `${funds[currentDataKey].previous_fund?.period}, ${funds[currentDataKey].previous_fund?.year}`
                    },
                    {
                      type: 'header',
                      text: `${funds[currentDataKey].period}, ${funds[currentDataKey].year}`
                    }
                  ]
                : [{ type: 'header', text: '' }])
            ]
          }
        ];
      } else {
        _rows = [
          {
            rowId: 0,
            height: getTextHeightForParagraph(value.name || '', 200),
            cells: [
              {
                type: 'header',
                text: ''
              },
              {
                type: 'header',
                text: value.name
              },
              ...(isComparisonReport
                ? Object.values(funds)
                    .map(item => [item, item.previous_fund])
                    .flat()
                    .map((fund, ind) => ({
                      type: 'header',
                      text: fund?.fund_name || '',
                      colspan: ind % 2 === 0 ? 2 : undefined
                    }))
                : Object.values(funds).map(value => ({
                    type: 'header',
                    text: value?.fund_name || ''
                  })))
            ]
          },
          ...(isComparisonReport
            ? [
                {
                  rowId: 1,
                  height: 40,
                  cells: [
                    {
                      type: 'header',
                      text: ''
                    },
                    {
                      type: 'header',
                      text: ''
                    },
                    ...Object.values(funds)
                      .map(item => [item.previous_fund, item])
                      .flat()
                      .map(fund => ({
                        type: 'header',
                        text: `${fund.period}, ${fund.year}`
                      }))
                  ]
                }
              ]
            : [])
        ];
      }
      return [
        ..._rows,
        ...Object.keys(value.columnLables).map((key, ind) => {
          let cells = [];
          if (isMultiFunds) {
            cells = isComparisonReport
              ? Object.values(funds)
                  .map(item => [item, item.previous_fund])
                  .flat()
                  .map((item, j) => {
                    const val =
                      tableName === 'HISTORICAL_PROFILE'
                        ? item.fund_data[tableName].find(
                            item => item.transaction_type === value.key
                          )
                        : item.fund_data[tableName][isSingleData ? 0 : index];
                    return getColumnByType({
                      type: schema.types[key].type,
                      options: schema.types[key].options,
                      key: key,
                      defaultValue: schema.defaultValues[key],
                      index: isSingleData ? 0 : index,
                      value: val,
                      required: requiredFields.includes(key),
                      tableName: tableName,
                      isStaticTemplate,
                      fundId: item.fund_id,
                      isSectionDisabled: isTableDisabledForFund(item.fund_id),
                      fundName: item.fund_name,
                      hasDrillDownFeature:
                        !!item.fund_data[tableName][isSingleData ? 0 : index]?.report_id &&
                        item.fund_data[tableName][isSingleData ? 0 : index]?.[key] !== null &&
                        schema.types[key].has_drill_down_feature,
                      reportId: item.fund_data[tableName][isSingleData ? 0 : index]?.report_id,
                      tableId: item.fund_data[tableName][isSingleData ? 0 : index]?.id,
                      title: value.columnLables[key]?.name,
                      isMultiFunds,
                      isEsma: true,
                      fieldOrder: value.order,
                      difference:
                        j % 2 === 1
                          ? funds[item.fund_id]?.difference?.[tableName]?.[
                              isSingleData ? 0 : index
                            ]?.[key] || {}
                          : null,
                      nonEditable: isComparisonReport
                    });
                  })
              : Object.values(funds).map(item => {
                  const val =
                    tableName === 'HISTORICAL_PROFILE'
                      ? item.fund_data[tableName].find(item => item.transaction_type === value.key)
                      : item.fund_data[tableName][isSingleData ? 0 : index];
                  return getColumnByType({
                    type: schema.types[key].type,
                    options: schema.types[key].options,
                    key: key,
                    defaultValue: schema.defaultValues[key],
                    index: isSingleData ? 0 : index,
                    value: val,
                    required: requiredFields.includes(key),
                    tableName: tableName,
                    isStaticTemplate,
                    fundId: item.fund_id,
                    isSectionDisabled: isTableDisabledForFund(item.fund_id),
                    fundName: item.fund_name,
                    hasDrillDownFeature:
                      !!item.fund_data[tableName][isSingleData ? 0 : index]?.report_id &&
                      item.fund_data[tableName][isSingleData ? 0 : index]?.[key] !== null &&
                      schema.types[key].has_drill_down_feature,
                    reportId: item.fund_data[tableName][isSingleData ? 0 : index]?.report_id,
                    tableId: item.fund_data[tableName][isSingleData ? 0 : index]?.id,
                    title: value.columnLables[key]?.name,
                    isMultiFunds,
                    isEsma: true,
                    fieldOrder: value.order,
                    nonEditable: isComparisonReport
                  });
                });
          } else {
            const val =
              tableName === 'HISTORICAL_PROFILE'
                ? data.find(item => item.transaction_type === value.key)
                : data[isSingleData ? 0 : index];
            const previousVal =
              tableName === 'HISTORICAL_PROFILE'
                ? funds[currentDataKey]?.previous_fund?.fund_data?.[tableName]?.find(
                    item => item.transaction_type === value.key
                  )
                : funds[currentDataKey]?.previous_fund?.fund_data?.[tableName]?.[
                    isSingleData ? 0 : index
                  ];
            cells = [
              ...(isComparisonReport
                ? [
                    getColumnByType({
                      type: schema.types[key].type,
                      options: schema.types[key].options,
                      key: key,
                      defaultValue: schema.defaultValues[key],
                      index: isSingleData ? 0 : index,
                      value: previousVal,
                      required: requiredFields.includes(key),
                      tableName: tableName,
                      isStaticTemplate,
                      isMultiFunds,
                      fundId: previousVal.fund_id,
                      isSectionDisabled: isTableDisabledForFund(previousVal),
                      fundName: previousVal.fund_name,
                      fieldOrder: value.order,
                      nonEditable: isComparisonReport
                    })
                  ]
                : []),
              getColumnByType({
                type: schema.types[key].type,
                options: schema.types[key].options,
                key: key,
                defaultValue: schema.defaultValues[key],
                index: isSingleData ? 0 : index,
                value: val,
                required: requiredFields.includes(key),
                tableName: tableName,
                isStaticTemplate,
                nonEditable: isComparisonReport,
                isMultiFunds,
                isEsma: true,
                fundId: val.fund_id,
                isSectionDisabled: isTableDisabledForFund(val.fund_id),
                fundName: val.fund_name,
                fieldOrder: value.order,
                difference: isComparisonReport
                  ? funds[currentDataKey]?.difference?.[tableName]?.[isSingleData ? 0 : index]?.[
                      key
                    ] || {}
                  : null
              })
            ];
          }

          // Each value column stacks its own value above its own error, so the
          // row must fit the tallest column — pairing the longest value with a
          // different column's longest error over-sizes it.
          const rows = isMultiFunds
            ? isComparisonReport
              ? Object.values(funds)
                  .map(fund => [fund.previous_fund, fund])
                  .flat()
                  .map(fund => fund.fund_data[tableName][isSingleData ? 0 : index])
              : Object.values(funds).map(
                  fund => fund.fund_data[tableName][isSingleData ? 0 : index]
                )
            : isComparisonReport
              ? [
                  funds[currentDataKey]?.previous_fund?.fund_data?.[tableName]?.[
                    isSingleData ? 0 : index
                  ],
                  data[isSingleData ? 0 : index]
                ]
              : [data[isSingleData ? 0 : index]];

          const rowHeight = getRowHeight({
            label: value.columnLables[key]?.name,
            labelWidth,
            cells: getRowCells(rows, key, schema?.types?.[key]?.options, false),
            valueWidth
          });

          return {
            rowId: isComparisonReport && isMultiFunds ? ind + 2 : ind + 1,
            height: rowHeight,
            cells: [
              {
                type: 'header',
                text: value.columnLables[key]?.order
                  ? `${value.columnLables[key]?.order}`
                  : `${value.order + ind}`
              },
              {
                type:
                  (!isMultiFunds &&
                    data[isSingleData ? 0 : index]?.report_id &&
                    data[isSingleData ? 0 : index]?.[key] !== null &&
                    schema.types[key].has_drill_down_feature) ||
                  value.columnLables[key].name.includes('*')
                    ? 'customHeader'
                    : 'header',
                text: value.columnLables[key]?.name || '',
                render: () => (
                  <CustomHeaderWithDrillDown
                    title={value.columnLables[key]?.name}
                    reportId={data[isSingleData ? 0 : index]?.report_id}
                    fundId={data[isSingleData ? 0 : index].fund_id}
                    columnName={key}
                    tableId={data[isSingleData ? 0 : index]?.id}
                    tableName={tableName}
                    hasDrillDown={!isMultiFunds && !!schema.types[key].has_drill_down_feature}
                  />
                ),
                style: {
                  'justify-content': 'flex-start',
                  'white-space': 'inherit'
                }
              },
              ...cells
            ]
          };
        })
      ];
    });
  }, [data, isMultiFunds, funds, isComparisonReport]);

  return { tables: _tables, columns, onChange };
}
